import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty, isNil } from 'lodash'
import queryString from 'query-string'

import { PageHeader } from '@/components/headers/PageHeader'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import Select from '@/components/forms/Select'
import { getOrganizationsForBaltoOrHierarchy } from '@/utils/helpers'
import {
  dismissInsight,
  fetchAgenticInsights,
  fetchRunStatus,
} from '@/reducers/insights/agenticInsights.actions'
import { setHasRuns } from '@/reducers/insights/agenticInsights.redux'
import { fetchPlaybooks } from '@/reducers/playbooks/playbooks.actions'

import {
  DRAWER_KIND,
  INSIGHTS_ERROR,
  SCOPE_ALL,
  VIEW,
  VIEW_LABEL,
  VIEW_ORDER,
} from './AgenticInsights.constants'
import {
  getActionableInsights,
  getInsightsInScope,
  getScopeSummary,
  isAgentInsight,
  isDismissed,
  isExecutiveInsight,
} from './AgenticInsights.helpers'
import { getCurrentRunInsights, getExperimentTabInsights } from './AgenticInsights.runReset'
import { RunScopeSelector } from './RunScopeSelector'
import { InsightsTab } from './InsightsTab'
import { ExperimentsTab } from './ExperimentsTab'
import { ExploreTab } from './ExploreTab'
import { InsightDrawer } from './InsightDrawer'

import './AgenticInsights.scss'

export const AgenticInsightsPage = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const queryOrgId = queryString.parse(location.search).org_id

  const {
    organizationid: currentUserOrg,
    hierarchy_manager: isHierarchyManager,
    own_organization_id: ownOrganizationId,
  } = useSelector((state) => state.currentUser)
  const { insights, loading, hasRuns, error } = useSelector((state) => state.agenticInsights)
  const { organizations } = useSelector((state) => state)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const allPlaybooks = useSelector((state) => state.playbooks?.playbooks || [])

  const isBaltoAdmin = currentUserOrg === 1
  const childOrgSelected = isHierarchyManager && ownOrganizationId !== currentUserOrg
  // Only orgs with the feature on are worth offering — picking one without it can
  // only ever produce a "not enabled" page. `GET /api/organizations` returns every
  // column on the org, so the flag is already here.
  const insightsOrganizations = organizations.filter(
    (organization) => organization.alex_insights_enabled
  )
  const { hasOrganizationDropdown, organizationOptions } = getOrganizationsForBaltoOrHierarchy({
    organizations: insightsOrganizations,
    flattenedUserOrgHierarchy,
    isBaltoAdmin,
    isHierarchyManager,
    childOrgSelected,
  })

  const [selectedOrgId, setSelectedOrgId] = useState(
    queryOrgId ? Number(queryOrgId) : currentUserOrg
  )
  const [view, setView] = useState(VIEW.INSIGHTS)
  const [scope, setScope] = useState(SCOPE_ALL)
  const [drawer, setDrawer] = useState(null)

  useEffect(() => {
    if (selectedOrgId) {
      dispatch(fetchAgenticInsights(selectedOrgId))
      dispatch(fetchRunStatus(selectedOrgId))
      dispatch(fetchPlaybooks(selectedOrgId))
    }
  }, [selectedOrgId])

  // A new run resets the page: Insights and Explore show only each filter set's
  // newest run, while Experiments keeps everything that was ever started. The
  // picker describes current runs only — an experiment that outlived its run
  // would otherwise keep an aged-out scope listed with that run's date and
  // counts. Those experiments stay visible under "All insights".
  const currentRunInsights = getCurrentRunInsights(insights)
  const experimentInsights = getExperimentTabInsights(insights)
  const scopeSummary = getScopeSummary(currentRunInsights, allPlaybooks)

  // Default to the most recent run's first playbook once data lands, so Insights
  // and Explore have something to show; fall back to All if we can't tell.
  useEffect(() => {
    if (scope === SCOPE_ALL && scopeSummary.scopes.length === 1) {
      setScope(scopeSummary.scopes[0].key)
    }
  }, [scopeSummary.scopes.length])

  const scopeInsights = getInsightsInScope(currentRunInsights, scope)
  // Dismissed insights still feed Explore's breakdown — they're real findings —
  // but no longer ask anyone to act on them.
  const undismissedScopeInsights = scopeInsights.filter((insight) => !isDismissed(insight))
  // Only the agent view shows the per-scope empty state, so only a dismissed
  // agent recommendation should change what it says.
  const hasDismissedInsights = getActionableInsights(scopeInsights).some(isDismissed)
  // A scope's newest run always has insights, so a scope with history but
  // nothing current has aged past the cutoff.
  const hasExpiredInsights = isEmpty(scopeInsights) && !isEmpty(getInsightsInScope(insights, scope))
  const sampleCalls = scopeInsights.find((insight) => insight.pipelineStats)?.pipelineStats
    ?.totalCalls

  const openInsight = (insightId) => {
    const insight = insights.find((item) => item.id === insightId)
    const kind =
      insight && isExecutiveInsight(insight) ? DRAWER_KIND.EXECUTIVE : DRAWER_KIND.INSIGHT
    setDrawer({ kind, insightId })
  }
  const openReason = (insightId) => {
    const insight = insights.find((item) => item.id === insightId)
    const kind =
      insight && isAgentInsight(insight) && insight.recommendation
        ? DRAWER_KIND.INSIGHT
        : DRAWER_KIND.REASON
    setDrawer({ kind, insightId })
  }
  const openInconclusive = (opportunity) =>
    setDrawer({ kind: DRAWER_KIND.INCONCLUSIVE, opportunity })

  return (
    <div className="agentic-insights-page agentic-insights-page--redesign">
      <PageHeader
        isDataGrid
        title="Agentic Insights"
        actions={
          isBaltoAdmin &&
          hasOrganizationDropdown && (
            <Select
              options={organizationOptions}
              value={selectedOrgId}
              onChange={(option) => {
                // Clear the previous org's run status so the empty-state copy falls
                // back to the neutral message while the new org's status loads,
                // rather than flashing the prior org's answer.
                dispatch(setHasRuns(null))
                setSelectedOrgId(option.value)
              }}
              placeholder="Select Organization"
              isClearable={false}
              fixedWidth
            />
          )
        }
      />

      <div className="agentic-insights-page__content">
        {loading && <Loader centered />}

        {!loading && error?.kind === INSIGHTS_ERROR.NOT_ENABLED && (
          <p className="agentic-muted">
            Agentic Insights isn&apos;t enabled for this organization.
          </p>
        )}

        {!loading && error?.kind === INSIGHTS_ERROR.NOT_PERMITTED && (
          <p className="agentic-muted">
            You don&apos;t currently have access to this organization&apos;s insights.
          </p>
        )}

        {!loading && error?.kind === INSIGHTS_ERROR.FETCH_FAILED && (
          <ErrorMessage content="We couldn't load Agentic Insights. Refresh to try again." />
        )}

        {!loading && isNil(error) && isEmpty(insights) && (
          <p className="agentic-muted">
            {hasRuns === false
              ? "We haven't run Agentic Insights for your org yet. Check back again soon."
              : 'Our latest analysis did not surface any insights for your organization. Check back again soon.'}
          </p>
        )}

        {/* Gated on the org's whole history, not what survives the run rules: an
            org whose insights all aged out still gets the tabs, which explain
            the 60-day cutoff instead of implying nothing was ever found. */}
        {!loading && !isEmpty(insights) && (
          <>
            <div className="agentic-insights-page__controls">
              {!isEmpty(scopeSummary.scopes) && (
                <RunScopeSelector summary={scopeSummary} scope={scope} onChange={setScope} />
              )}
            </div>
            {sampleCalls != null && (
              <p className="agentic-basis">
                We analyzed <b>{sampleCalls.toLocaleString()}</b> of your calls from this run.
              </p>
            )}

            <SegmentedControl
              className="agentic-insights-page__tabs"
              items={VIEW_ORDER.map((viewKey) => ({
                accessor: viewKey,
                active: view === viewKey,
                onClick: () => setView(viewKey),
                label: VIEW_LABEL[viewKey],
                dataTestId: `agentic-view-${viewKey}`,
              }))}
            />

            {view === VIEW.INSIGHTS && (
              <InsightsTab
                scopeInsights={undismissedScopeInsights}
                scope={scope}
                playbooks={allPlaybooks}
                hasExpiredInsights={hasExpiredInsights}
                hasDismissedInsights={hasDismissedInsights}
                onOpen={openInsight}
                onDismiss={(insightId) => dispatch(dismissInsight(insightId))}
              />
            )}
            {view === VIEW.EXPERIMENTS && (
              <ExperimentsTab
                scopeInsights={getInsightsInScope(experimentInsights, scope).filter(
                  (insight) => !isDismissed(insight)
                )}
                scope={scope}
                playbooks={allPlaybooks}
                onOpen={openInsight}
                onGoTo={setView}
              />
            )}
            {view === VIEW.EXPLORE && (
              <ExploreTab
                scopeInsights={scopeInsights}
                scope={scope}
                hasExpiredInsights={hasExpiredInsights}
                onOpenReason={openReason}
                onOpenInconclusive={openInconclusive}
              />
            )}
          </>
        )}
      </div>

      {drawer && (
        <InsightDrawer
          drawer={drawer}
          insights={insights}
          playbooks={allPlaybooks}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  )
}

export default AgenticInsightsPage
